import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { message } from "antd";

import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { AGENT_ACTIONS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatProviderLabel } from "../../../../utils/formatProviderLabel";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import AppContext from "../../../../context/AppContext";
import RebetLoader from "../../../shared/RebetLoader/RebetLoader";
import ToggleSwitch from "../../../shared/ToggleSwitch/ToggleSwitch";

import styles from "./ApplePayV2Routing.module.css";

/** A routing band as returned/sent by the API. */
interface ApplePayRoutingRule {
  provider: string;
  min: number;
  max: number;
}

interface ApplePayRoutingData {
  enabled?: boolean;
  rules?: ApplePayRoutingRule[];
}

interface ApplePayRoutingResponse {
  data?: ApplePayRoutingData;
  available_providers?: string[];
}

/** UI row state — amounts are kept as strings so inputs can be empty / mid-edit. */
interface ProviderRow {
  provider: string;
  label: string;
  enabled: boolean;
  min: string;
  max: string;
}

const EN_DASH = "\u2013";
const EM_DASH = "\u2014";

// Boundary step between adjacent ranges (penny granularity): one provider
// ends at 50.00 and the next starts at 50.01, leaving no unroutable amounts.
const RANGE_STEP = 0.01;

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const getApiInstance = () =>
  createPrivateAdminPanelInstance(
    JSON.parse(localStorage.getItem("token") || "null")?.AccessToken,
  );

const toInputAmount = (value: number | string | undefined | null): string => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? "" : numeric.toFixed(2);
};

const isFilled = (value: string): boolean =>
  value !== "" && !Number.isNaN(Number(value));

const formatMoney = (value: number, symbol: string): string =>
  `${symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Build the row list from the API payload. Rows in `data.rules` come first (in
 * API order, all enabled); any `available_providers` not present in `rules` are
 * appended as toggled-off rows with empty ranges — a provider absent from the
 * saved rules is one the operator previously disabled.
 */
const buildRows = (
  data: ApplePayRoutingData,
  availableProviders: string[],
): ProviderRow[] => {
  const rules = Array.isArray(data?.rules) ? data.rules : [];
  const rowsFromRules: ProviderRow[] = rules
    .filter((rule) => rule?.provider)
    .map((rule) => ({
      provider: rule.provider,
      label: formatProviderLabel(rule.provider),
      enabled: true,
      min: toInputAmount(rule.min),
      max: toInputAmount(rule.max),
    }));

  const seen = new Set(rowsFromRules.map((row) => row.provider));
  const rowsFromAvailable: ProviderRow[] = availableProviders
    .filter((provider) => provider && !seen.has(provider))
    .map((provider) => ({
      provider,
      label: formatProviderLabel(provider),
      enabled: false,
      min: "",
      max: "",
    }));

  return [...rowsFromRules, ...rowsFromAvailable];
};

const cloneRows = (rows: ProviderRow[]): ProviderRow[] =>
  rows.map((row) => ({ ...row }));

const normalizeRows = (rows: ProviderRow[]): string =>
  JSON.stringify(
    rows.map(({ provider, enabled, min, max }) => ({
      provider,
      enabled,
      min,
      max,
    })),
  );

interface OverlapResult {
  first: ProviderRow;
  second: ProviderRow;
  low: number;
  high: number;
}

/** First overlapping pair among enabled rows (list order); the earlier row wins. */
const findOverlap = (rows: ProviderRow[]): OverlapResult | null => {
  const active = rows
    .filter((row) => row.enabled && isFilled(row.min) && isFilled(row.max))
    .map((row) => ({ row, min: Number(row.min), max: Number(row.max) }));

  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const low = Math.max(active[i].min, active[j].min);
      const high = Math.min(active[i].max, active[j].max);
      if (low <= high) {
        return {
          first: active[i].row,
          second: active[j].row,
          low,
          high,
        };
      }
    }
  }
  return null;
};

interface RangeGap {
  /** Upper bound of the range below the gap. */
  from: number;
  /** Lower bound of the range above the gap. */
  to: number;
}

/**
 * Uncovered stretches between consecutive enabled ranges (sorted by lower
 * bound). Adjacent ranges separated by exactly RANGE_STEP are contiguous per
 * the BE convention; anything wider leaves deposits with no provider.
 */
const findGaps = (rows: ProviderRow[]): RangeGap[] => {
  const active = rows
    .filter((row) => row.enabled && isFilled(row.min) && isFilled(row.max))
    .map((row) => ({ min: Number(row.min), max: Number(row.max) }))
    .sort((a, b) => a.min - b.min);

  const gaps: RangeGap[] = [];
  for (let i = 0; i < active.length - 1; i += 1) {
    // Float-safe: 50.1 - 50 evaluates just under 0.1, so pad the comparison.
    if (active[i + 1].min - active[i].max > RANGE_STEP + 0.001) {
      gaps.push({ from: active[i].max, to: active[i + 1].min });
    }
  }
  return gaps;
};

const WarningIcon = () => (
  <svg
    className={styles.warningIcon}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 4.5v4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11" r="0.9" fill="currentColor" />
  </svg>
);

const ApplePayV2Routing = () => {
  const appContext = useContext(AppContext) as
    | { adminActions?: string[] }
    | undefined;
  const canEdit = !!appContext?.adminActions?.includes(
    AGENT_ACTIONS?.EDIT_TRANSACTION_ROUTING,
  );

  const currencySymbol = useCurrencySymbol() ?? "$";

  const [rows, setRows] = useState<ProviderRow[]>([]);
  const [snapshotRows, setSnapshotRows] = useState<ProviderRow[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  /**
   * Load settings from the API. Returns whether the fetch succeeded.
   * `background` keeps the card interactive during the request (no full-card
   * loader) and keeps a failure from flipping into the error screen — used
   * by the post-save refetch, where the save itself already succeeded and
   * the Save button's own spinner covers the wait.
   */
  const fetchSettings = useCallback(
    async (options?: { background?: boolean }) => {
      if (!options?.background) {
        setLoading(true);
        setLoadError(false);
      }
      try {
        const response = await getApiInstance().get(
          API_ROUTES.APPLE_PAY_PROVIDER_ROUTING,
        );
        const payload: ApplePayRoutingResponse = response?.data ?? {};
        const data: ApplePayRoutingData = payload?.data ?? {};
        const availableProviders = Array.isArray(payload?.available_providers)
          ? payload.available_providers
          : [];

        const built = buildRows(data, availableProviders);
        setRows(built);
        setSnapshotRows(cloneRows(built));
        return true;
      } catch (error) {
        console.error("error getting Apple Pay provider routing.", error);
        if (!options?.background) {
          setLoadError(true);
        }
        return false;
      } finally {
        if (!options?.background) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const hasUnsavedChanges = useMemo(
    () => normalizeRows(rows) !== normalizeRows(snapshotRows),
    [rows, snapshotRows],
  );

  const enabledRows = useMemo(
    () => rows.filter((row) => row.enabled),
    [rows],
  );

  const overlap = useMemo(() => findOverlap(rows), [rows]);

  const gaps = useMemo(() => findGaps(rows), [rows]);

  // Rows whose bounds were pushed inside-out (min above max), e.g. by an
  // auto-adjustment from a neighbour or a direct edit.
  const invalidRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.enabled &&
          isFilled(row.min) &&
          isFilled(row.max) &&
          Number(row.min) > Number(row.max),
      ),
    [rows],
  );

  const handleToggle = (provider: string, isActive: boolean) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row.provider === provider);
      if (index === -1) {
        return prev;
      }
      const next = cloneRows(prev);

      if (!isActive) {
        // Turning off: the nearest enabled neighbour absorbs this row's range so
        // the remaining bands stay contiguous. Prefer the next row (extend its
        // lower bound down); fall back to the previous row (extend its upper
        // bound up) when this was the last enabled row.
        const offRow = next[index];
        let neighbour = -1;
        for (let i = index + 1; i < next.length; i += 1) {
          if (next[i].enabled) {
            neighbour = i;
            break;
          }
        }
        if (neighbour !== -1) {
          next[neighbour].min = offRow.min;
        } else {
          for (let i = index - 1; i >= 0; i -= 1) {
            if (next[i].enabled) {
              neighbour = i;
              break;
            }
          }
          if (neighbour !== -1) {
            next[neighbour].max = offRow.max;
          }
        }
        next[index].enabled = false;
      } else {
        // Turning on: the row continues from the nearest enabled neighbour
        // without shrinking it — min picks up right after the neighbour
        // above's current max (max + step). Its own upper bound is kept only
        // when it still fits above the new min; otherwise it's cleared and
        // save stays blocked until the operator fills it.
        const target = next[index];
        let prevIdx = -1;
        for (let i = index - 1; i >= 0; i -= 1) {
          if (next[i].enabled && isFilled(next[i].max)) {
            prevIdx = i;
            break;
          }
        }
        let nextIdx = -1;
        for (let i = index + 1; i < next.length; i += 1) {
          if (next[i].enabled && isFilled(next[i].min)) {
            nextIdx = i;
            break;
          }
        }

        if (prevIdx !== -1) {
          const newMin = roundMoney(Number(next[prevIdx].max) + RANGE_STEP);
          target.min = toInputAmount(newMin);
          if (!(isFilled(target.max) && Number(target.max) > newMin)) {
            target.max = "";
          }
        } else if (nextIdx !== -1) {
          // Mirror case (no enabled row above): slot in below the neighbour's
          // min, leaving the neighbour untouched.
          const newMax = roundMoney(Number(next[nextIdx].min) - RANGE_STEP);
          target.max = toInputAmount(newMax);
          if (!(isFilled(target.min) && Number(target.min) < newMax)) {
            target.min = "";
          }
        } else {
          // No enabled neighbour to continue from — fall back to this row's
          // last-loaded bounds (may be empty).
          const snap = snapshotRows.find((row) => row.provider === provider);
          target.min = snap ? snap.min : target.min;
          target.max = snap ? snap.max : target.max;
        }
        target.enabled = true;
      }
      return next;
    });
  };

  const handleAmountChange = (
    provider: string,
    field: "min" | "max",
    value: string,
  ) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row.provider === provider);
      if (index === -1) {
        return prev;
      }
      const next = cloneRows(prev);
      next[index][field] = value;

      // Keep the bands contiguous while editing: raising a max drags the next
      // enabled provider's min along (max + step); lowering a min drags the
      // previous enabled provider's max (min - step). Disabled rows are
      // skipped — they're excluded from the payload anyway.
      const numeric = Number(value);
      if (value === "" || Number.isNaN(numeric)) {
        return next;
      }
      if (field === "max") {
        for (let i = index + 1; i < next.length; i += 1) {
          if (next[i].enabled) {
            next[i].min = toInputAmount(roundMoney(numeric + RANGE_STEP));
            break;
          }
        }
      } else {
        for (let i = index - 1; i >= 0; i -= 1) {
          if (next[i].enabled) {
            next[i].max = toInputAmount(roundMoney(numeric - RANGE_STEP));
            break;
          }
        }
      }
      return next;
    });
  };

  const handleAmountBlur = (provider: string, field: "min" | "max") => {
    setRows((prev) =>
      prev.map((row) =>
        row.provider === provider && isFilled(row[field])
          ? { ...row, [field]: toInputAmount(row[field]) }
          : row,
      ),
    );
  };

  const handleDiscard = () => {
    setRows(cloneRows(snapshotRows));
  };

  const handleSave = async () => {
    const hasEmptyRange = enabledRows.some(
      (row) => !isFilled(row.min) || !isFilled(row.max),
    );
    if (hasEmptyRange) {
      message.error(AppStrings.APPLE_PAY_ROUTING_FILL_ALL_RANGES);
      return;
    }

    const hasNonPositiveAmount = enabledRows.some(
      (row) => Number(row.min) <= 0 || Number(row.max) <= 0,
    );
    if (hasNonPositiveAmount) {
      message.error(AppStrings.APPLE_PAY_ROUTING_POSITIVE_AMOUNTS);
      return;
    }

    if (invalidRows.length > 0) {
      message.error(AppStrings.APPLE_PAY_ROUTING_INVALID_BLOCK_SAVE);
      return;
    }

    if (gaps.length > 0) {
      message.error(AppStrings.APPLE_PAY_ROUTING_GAPS_BLOCK_SAVE);
      return;
    }

    setSaving(true);
    try {
      // `enabled` mirrors whether any provider is on: turning the last
      // provider off saves `enabled: false` with no rules, and re-enabling
      // one flips it back to true.
      await getApiInstance().patch(API_ROUTES.APPLE_PAY_PROVIDER_ROUTING, {
        enabled: enabledRows.length > 0,
        rules: enabledRows.map((row) => ({
          provider: row.provider,
          min: Number(row.min),
          max: Number(row.max),
        })),
      });
      message.success(AppStrings.APPLE_PAY_ROUTING_SAVE_SUCCESS);
      // Reload from the server so the UI reflects the canonical saved state
      // (and the snapshot/dirty tracking resets from it). If the refetch
      // fails the save still went through — keep the just-saved local rows
      // as the new baseline instead of flashing the error screen.
      const refreshed = await fetchSettings({ background: true });
      if (!refreshed) {
        setSnapshotRows(cloneRows(rows));
      }
    } catch (error) {
      const responseMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      message.error(
        responseMessage ?? AppStrings.APPLE_PAY_ROUTING_SAVE_ERROR,
      );
    } finally {
      setSaving(false);
    }
  };

  const buildSubtitle = (row: ProviderRow): string => {
    const routes = AppStrings.APPLE_PAY_ROUTING_ROUTES;
    if (!isFilled(row.min) && !isFilled(row.max)) {
      return `${routes} ${EN_DASH}`;
    }
    const min = isFilled(row.min)
      ? formatMoney(Number(row.min), currencySymbol)
      : EN_DASH;
    const max = isFilled(row.max)
      ? formatMoney(Number(row.max), currencySymbol)
      : EN_DASH;
    return `${routes} ${min} ${EN_DASH} ${max}`;
  };

  const overlapText = overlap
    ? `${overlap.first.label} ${AppStrings.AND} ${overlap.second.label} ${
        AppStrings.APPLE_PAY_ROUTING_BOTH_MATCH
      } ${formatMoney(overlap.low, currencySymbol)} ${EN_DASH} ${formatMoney(
        overlap.high,
        currencySymbol,
      )} ${EM_DASH} ${overlap.first.label} ${
        AppStrings.APPLE_PAY_ROUTING_WINS_OVERLAP
      }`
    : "";

  const saveDisabled = !canEdit || saving || !hasUnsavedChanges;
  const discardDisabled = !canEdit || saving || !hasUnsavedChanges;

  const renderBody = () => {
    if (loading) {
      return (
        <div className={styles.stateBox} data-testid="ap-routing-loading">
          <RebetLoader height="80px" width="80px" />
        </div>
      );
    }

    if (loadError) {
      return (
        <div className={styles.stateBox} data-testid="ap-routing-error">
          <p className={styles.stateText}>
            {AppStrings.APPLE_PAY_ROUTING_LOAD_ERROR}
          </p>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => fetchSettings()}
            data-testid="ap-routing-retry"
          >
            {AppStrings.RETRY}
          </button>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className={styles.stateBox} data-testid="ap-routing-empty">
          <p className={styles.stateText}>
            {AppStrings.APPLE_PAY_ROUTING_EMPTY}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.rows}>
          {rows.map((row) => {
            // Freeze edits while a save (and its refetch) is in flight so
            // in-flight changes can't be silently overwritten by the reload.
            const inputsDisabled = !canEdit || saving || !row.enabled;
            return (
              <div
                key={row.provider}
                className={`${styles.row} ${
                  row.enabled ? "" : styles.rowDisabled
                }`}
                data-testid={`ap-routing-row-${row.provider}`}
              >
                <div className={styles.rowLeft}>
                  <ToggleSwitch
                    tone="info"
                    status={row.enabled}
                    eventCode={row.provider}
                    updateEventCode={({ is_active }) =>
                      handleToggle(row.provider, is_active)
                    }
                    disabled={!canEdit || saving}
                  />
                  <div className={styles.avatar} aria-hidden="true">
                    {row.label.charAt(0)}
                  </div>
                  <div className={styles.info}>
                    <div className={styles.name}>{row.label}</div>
                    <div className={styles.range}>{buildSubtitle(row)}</div>
                  </div>
                </div>
                <div className={styles.rowRight}>
                  <div className={styles.inputWrap}>
                    <span className={styles.currency}>{currencySymbol}</span>
                    <input
                      type="number"
                      className={styles.amount}
                      value={row.min}
                      min={RANGE_STEP}
                      step={RANGE_STEP}
                      onChange={(e) =>
                        handleAmountChange(row.provider, "min", e.target.value)
                      }
                      onBlur={() => handleAmountBlur(row.provider, "min")}
                      disabled={inputsDisabled}
                      data-testid={`ap-routing-min-${row.provider}`}
                    />
                  </div>
                  <span className={styles.to}>{AppStrings.TO}</span>
                  <div className={styles.inputWrap}>
                    <span className={styles.currency}>{currencySymbol}</span>
                    <input
                      type="number"
                      className={styles.amount}
                      value={row.max}
                      min={RANGE_STEP}
                      step={RANGE_STEP}
                      onChange={(e) =>
                        handleAmountChange(row.provider, "max", e.target.value)
                      }
                      onBlur={() => handleAmountBlur(row.provider, "max")}
                      disabled={inputsDisabled}
                      data-testid={`ap-routing-max-${row.provider}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {overlap && (
          <div className={styles.warning} data-testid="ap-routing-overlap">
            <WarningIcon />
            <span>{overlapText}</span>
          </div>
        )}

        {gaps.map((gap) => (
          <div
            key={`${gap.from}-${gap.to}`}
            className={styles.warning}
            data-testid="ap-routing-gap"
          >
            <WarningIcon />
            <span>
              {`${AppStrings.APPLE_PAY_ROUTING_GAP_BETWEEN} ${formatMoney(
                gap.from,
                currencySymbol,
              )} ${AppStrings.AND} ${formatMoney(
                gap.to,
                currencySymbol,
              )} ${EM_DASH} ${AppStrings.APPLE_PAY_ROUTING_GAP_FIX}`}
            </span>
          </div>
        ))}

        {invalidRows.map((row) => (
          <div
            key={row.provider}
            className={styles.errorBanner}
            data-testid={`ap-routing-invalid-${row.provider}`}
          >
            <WarningIcon />
            <span>
              {`${row.label} ${EM_DASH} ${AppStrings.APPLE_PAY_ROUTING_INVALID_RANGE}`}
            </span>
          </div>
        ))}

        <div className={styles.footer}>
          <span className={styles.dirty}>
            {hasUnsavedChanges && (
              <span data-testid="ap-routing-unsaved">
                <span className={styles.dot} aria-hidden="true" />
                {AppStrings.APPLE_PAY_ROUTING_UNSAVED_CHANGES}
              </span>
            )}
          </span>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleDiscard}
              disabled={discardDisabled}
              data-testid="ap-routing-discard"
            >
              {AppStrings.APPLE_PAY_ROUTING_DISCARD}
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSave}
              disabled={saveDisabled}
              data-testid="ap-routing-save"
            >
              {saving ? (
                <RebetLoader height="20px" width="20px" />
              ) : (
                AppStrings.SAVE_CHANGES
              )}
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.card} data-testid="apple-pay-v2-routing">
      <div className={styles.header}>
        <span className={styles.eyebrow}>{AppStrings.PAYMENT_ROUTING}</span>
        <h2 className={styles.title}>{AppStrings.APPLE_PAY_ROUTING}</h2>
        <p className={styles.subtitle}>
          {AppStrings.APPLE_PAY_ROUTING_DESCRIPTION}
        </p>
      </div>
      {renderBody()}
    </div>
  );
};

export default ApplePayV2Routing;
